import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, Eye, MousePointerClick, ShieldCheck, Upload, Building2, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, naira } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AD_STATUS = {
  pending: { label: "awaiting payment", c: "text-[#FFD700]" },
  pending_review: { label: "under review", c: "text-[#FFB300]" },
  approved_unpaid: { label: "approved — pay to go live", c: "text-[#00E5FF]" },
  active: { label: "live", c: "text-[#16A34A]" },
  rejected: { label: "rejected", c: "text-[#FF0055]" },
  removed: { label: "taken down", c: "text-[#FF0055]" },
  expired: { label: "expired", c: "text-slate-500" },
};
const STATUS_DOT = { available: "🟢", reserved: "🟡", sold: "🔴", unavailable: "⚫" };

export default function Advertise() {
  const nav = useNavigate();
  const [me, setMe] = useState(undefined); // undefined=loading, null=logged out, obj=advertiser
  const [tab, setTab] = useState("login");
  const [authForm, setAuthForm] = useState({ email: "", password: "", business_name: "", contact_name: "", phone: "", industry: "", country: "Nigeria", website: "" });
  const [authBusy, setAuthBusy] = useState(false);

  const [inventory, setInventory] = useState([]);
  const [packages, setPackages] = useState([]);
  const [mine, setMine] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ advertiser_name: "", image_url: "", link_url: "", package_id: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const checkMe = useCallback(async () => {
    try { setMe((await api.get("/advertiser/me")).data.advertiser); }
    catch { setMe(null); }
  }, []);
  useEffect(() => { checkMe(); }, [checkMe]);

  const loadMarket = useCallback(async () => {
    try {
      const [inv, pk, m] = await Promise.all([api.get("/ad-inventory"), api.get("/ad-packages"), api.get("/ads/mine")]);
      setInventory(inv.data.inventory); setPackages(pk.data.packages); setMine(m.data.ads);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { if (me) loadMarket(); }, [me, loadMarket]);

  const doAuth = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    try {
      if (tab === "login") await api.post("/auth/login", { email: authForm.email, password: authForm.password });
      else await api.post("/advertiser/register", authForm);
      await checkMe();
      toast.success(tab === "login" ? "Welcome back!" : "Advertiser account created 🎉");
    } catch (e) { toast.error(errMsg(e)); } finally { setAuthBusy(false); }
  };

  const logout = async () => { try { await api.post("/auth/logout"); } catch (e) { if (process.env.NODE_ENV === "development") console.debug("logout failed", e); } setMe(null); };

  const price = sel && form.package_id ? Math.round(sel.base_price * (packages.find((p) => p.id === form.package_id)?.price_multiplier || 1)) : sel?.base_price;
  const days = packages.find((p) => p.id === form.package_id)?.days;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/advertiser/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: `${BACKEND}${data.path}` }));
      toast.success("Creative uploaded ✓");
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };

  const buy = async (e) => {
    e.preventDefault();
    if (!form.package_id) { toast.error("Pick a package."); return; }
    if (!form.image_url.startsWith("http")) { toast.error("Upload a creative first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ads/buy", { slot_id: sel.id, ...form, callback_url: `${window.location.origin}/payment/callback` });
      if (data.authorization_url) { window.location.href = data.authorization_url; return; }
      toast.success(data.message || "Ad submitted for review.");
      setSel(null); setForm({ advertiser_name: me?.business_name || "", image_url: "", link_url: "", package_id: "" });
      await loadMarket();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const payNow = async (id) => {
    try { const { data } = await api.post(`/ads/pay/${id}`, { callback_url: `${window.location.origin}/payment/callback` }); window.location.href = data.authorization_url; }
    catch (e) { toast.error(errMsg(e)); }
  };

  // ---------- Auth gate ----------
  if (me === undefined) return <div className="min-h-[100dvh] bg-[#F8FAFC] flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" /></div>;

  if (!me) {
    return (
      <div className="min-h-[100dvh] bg-[#F8FAFC] px-5 py-8 max-w-md mx-auto" data-testid="advertiser-auth">
        <div className="flex items-center gap-2"><Megaphone className="text-[#FFD700]" size={26} /><h1 className="font-display text-3xl">Advertise With Us</h1></div>
        <p className="text-sm text-slate-500 mt-1">Put your brand where players live, work, shop & socialize. Reach thousands inside the game.</p>
        <div className="mt-5 flex gap-2">
          <button data-testid="tab-login" onClick={() => setTab("login")} className={`flex-1 py-2 rounded-lg label-xs font-bold ${tab === "login" ? "bg-[#FFD700] text-slate-900" : "bg-white text-slate-500"}`}>LOG IN</button>
          <button data-testid="tab-register" onClick={() => setTab("register")} className={`flex-1 py-2 rounded-lg label-xs font-bold ${tab === "register" ? "bg-[#FFD700] text-slate-900" : "bg-white text-slate-500"}`}>CREATE ACCOUNT</button>
        </div>
        <form onSubmit={doAuth} className="mt-4 space-y-2.5">
          {tab === "register" && <>
            <input data-testid="adv-business" required placeholder="Business name" value={authForm.business_name} onChange={(e) => setAuthForm({ ...authForm, business_name: e.target.value })} className="input-dark" />
            <input data-testid="adv-contact" required placeholder="Contact person" value={authForm.contact_name} onChange={(e) => setAuthForm({ ...authForm, contact_name: e.target.value })} className="input-dark" />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Phone / WhatsApp" value={authForm.phone} onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })} className="input-dark" />
              <input placeholder="Industry" value={authForm.industry} onChange={(e) => setAuthForm({ ...authForm, industry: e.target.value })} className="input-dark" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Country" value={authForm.country} onChange={(e) => setAuthForm({ ...authForm, country: e.target.value })} className="input-dark" />
              <input placeholder="Website" value={authForm.website} onChange={(e) => setAuthForm({ ...authForm, website: e.target.value })} className="input-dark" />
            </div>
          </>}
          <input data-testid="adv-email" required type="email" placeholder="Email" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} className="input-dark" />
          <input data-testid="adv-password" required type="password" placeholder="Password" value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} className="input-dark" />
          <button data-testid="adv-submit" disabled={authBusy} className="w-full bg-[#FFD700] text-slate-900 font-bold py-3 rounded-xl disabled:opacity-50">{authBusy ? "…" : tab === "login" ? "Log In" : "Create Advertiser Account"}</button>
        </form>
        <button onClick={() => nav("/")} className="mt-4 w-full label-xs text-slate-500">← Back to game</button>
        <style>{`.input-dark{width:100%;background:#F1F5F9;border:1px solid #e2e8f0;border-radius:0.75rem;padding:0.6rem 0.9rem;color:#0F172A;font-size:0.875rem}.input-dark::placeholder{color:#94a3b8}.input-dark:focus{outline:none;border-color:#FFD700}`}</style>
      </div>
    );
  }

  // ---------- Portal ----------
  return (
    <div className="min-h-[100dvh] bg-[#F8FAFC] px-5 py-6 max-w-2xl mx-auto" data-testid="advertise-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Building2 className="text-[#FFD700]" size={22} /><div><div className="font-display text-xl leading-none">{me.business_name}</div><div className="label-xs text-slate-500">Advertiser dashboard</div></div></div>
        <button data-testid="adv-logout" onClick={logout} className="label-xs text-slate-500 flex items-center gap-1"><LogOut size={13} /> Log out</button>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[0.7rem] text-slate-500 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 w-fit">
        <ShieldCheck size={13} className="text-[#16A34A]" /> Every ad is AI-reviewed, then human-approved before it goes live.
      </div>

      {!sel ? (
        <>
          <h2 className="font-display text-lg mt-5 mb-2">Ad marketplace</h2>
          <div className="space-y-2.5">
            {inventory.map((s) => (
              <div key={s.id} data-testid={`ad-slot-${s.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{STATUS_DOT[s.status] || ""} {s.name}</div>
                  <div className="label-xs text-slate-500 mt-1 capitalize">{s.category} · {s.city}{s.neighborhood && s.neighborhood !== "—" ? ` · ${s.neighborhood}` : ""}</div>
                  <div className="text-[#16A34A] font-display mt-1">from {naira(s.base_price)}</div>
                </div>
                {s.status === "available" ? (
                  <button data-testid={`ad-pick-${s.id}`} onClick={() => { setSel(s); setForm({ advertiser_name: me.business_name, image_url: "", link_url: "", package_id: packages[0]?.id || "" }); }} className="label-xs bg-[#FFD700] text-slate-900 px-4 py-2 rounded-lg font-bold">Book</button>
                ) : <span className="label-xs text-slate-400 capitalize">{s.status}</span>}
              </div>
            ))}
          </div>

          {mine.length > 0 && (
            <div className="mt-7 mb-6">
              <h2 className="font-display text-lg mb-2">Your campaigns</h2>
              <div className="flex gap-2 mb-3">
                <Stat label="Impressions" val={mine.reduce((a, b) => a + (b.impressions || 0), 0)} />
                <Stat label="Clicks" val={mine.reduce((a, b) => a + (b.clicks || 0), 0)} />
                <Stat label="Active" val={mine.filter((a) => a.status === "active").length} />
              </div>
              <div className="space-y-2">
                {mine.map((a) => {
                  const st = AD_STATUS[a.status] || { label: a.status, c: "text-slate-500" };
                  const note = a.admin_note || a.ai_review?.reason;
                  return (
                    <div key={a.id} data-testid={`my-ad-${a.id}`} className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <img src={a.image_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-black" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{a.advertiser_name}</div>
                          <div className="label-xs text-slate-500">{a.slot_name} · <span className={st.c}>{st.label}</span></div>
                        </div>
                        <div className="text-right label-xs text-slate-500">
                          <div className="flex items-center gap-1 justify-end"><Eye size={11} /> {a.impressions}</div>
                          <div className="flex items-center gap-1 justify-end mt-0.5"><MousePointerClick size={11} /> {a.clicks}</div>
                        </div>
                      </div>
                      {(a.status === "rejected" || a.status === "pending_review" || a.status === "removed") && note && (
                        <div className="mt-2 text-[0.7rem] text-slate-500 border-t border-slate-200 pt-2">{note}</div>
                      )}
                      {a.status === "approved_unpaid" && (
                        <button data-testid={`my-ad-pay-${a.id}`} onClick={() => payNow(a.id)} className="mt-2 w-full label-xs bg-[#FFD700] text-slate-900 py-2 rounded-lg font-bold">Approved ✓ — Complete payment</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        <form onSubmit={buy} className="mt-5 space-y-3 mb-6" data-testid="ad-create-form">
          <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-xl p-3">
            <div className="font-semibold text-[#FFD700]">{sel.name}</div>
            <div className="label-xs text-slate-500 mt-1">Base {naira(sel.base_price)} · {sel.category}</div>
          </div>

          <div>
            <span className="label-xs text-slate-500">Package</span>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {packages.map((p) => (
                <button type="button" key={p.id} data-testid={`pkg-${p.id}`} onClick={() => setForm({ ...form, package_id: p.id })}
                  className={`text-left rounded-xl border p-2.5 ${form.package_id === p.id ? "border-[#FFD700] bg-[#FFD700]/10" : "border-slate-200 bg-white"}`}>
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="label-xs text-slate-500">{p.days} days</div>
                  <div className="label-xs text-[#16A34A] mt-0.5">{naira(Math.round(sel.base_price * p.price_multiplier))}</div>
                </button>
              ))}
            </div>
          </div>

          <input data-testid="ad-name" required value={form.advertiser_name} onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })} placeholder="Display / business name" className="input-dark" />
          <input data-testid="ad-link" required value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="Destination link (https://…)" className="input-dark" />

          <label data-testid="ad-upload" className="flex items-center justify-center gap-2 w-full bg-white border border-dashed border-slate-200 rounded-xl py-4 cursor-pointer text-slate-500 label-xs">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {form.image_url ? "Replace creative" : "Upload creative (JPG/PNG/WebP)"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          {form.image_url && <img src={form.image_url} alt="preview" className="w-full h-28 object-cover rounded-xl border border-slate-200" />}

          <button data-testid="ad-pay" disabled={busy || uploading} className="w-full bg-[#FFD700] text-slate-900 font-bold py-3.5 rounded-xl disabled:opacity-50">
            {busy ? "Opening checkout…" : `Pay ${naira(price)} · ${days || 7} days`}
          </button>
          <button type="button" onClick={() => setSel(null)} className="w-full py-2 label-xs text-slate-500">Cancel</button>
          <style>{`.input-dark{width:100%;background:#F1F5F9;border:1px solid #e2e8f0;border-radius:0.75rem;padding:0.75rem 1rem;color:#0F172A;font-size:0.875rem}.input-dark::placeholder{color:#94a3b8}.input-dark:focus{outline:none;border-color:#FFD700}`}</style>
        </form>
      )}
    </div>
  );
}

function Stat({ label, val }) {
  return (
    <div className="flex-1 bg-white border border-slate-200 rounded-xl p-2.5 text-center">
      <div className="font-display text-lg text-[#FFD700]">{val}</div>
      <div className="label-xs text-slate-500">{label}</div>
    </div>
  );
}
